import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useOrganization } from '@/hooks/useOrganization';
import { DataModelAttribute } from '@/types/DataModel';

const usePrecisionAndScale = (attribute: DataModelAttribute) => {

    const intl = useIntl();
    const { organization } = useOrganization();

    /**
     * Applies the precision and scale rules defined in the data model attribute, where:
     *   - ex: 123.456 => precision = 6
     *   - ex: 123.456 => scale/fraction = 3
     */
    const applyPrecisionAndScale = useCallback((value: string) => {

        let decimalSeparator = '.';
        const currentLocalDecimalSeparator = intl.formatNumber(1.1).charAt(1);;

        // Set the decimal separator if changed by organization settings
        if (decimalSeparator !== currentLocalDecimalSeparator && organization?.localeSpecificNumberFormat) {
            decimalSeparator = currentLocalDecimalSeparator;
        }

        const maxPrecision = attribute.params?.maxPrecision;
        const maxScale = attribute.params?.maxScale;

        if (maxPrecision && value) {
            // Truncate the whole value if it exceeds maxPrecision
            // The separator is not actually included in the allowed precision so we add 1 if it's in the value
            const actualMaxPrecision = value.includes(decimalSeparator) ? maxPrecision + 1 : maxPrecision;
            value = value.slice(0, actualMaxPrecision);
        }

        const parts = value.split(decimalSeparator);
        if (maxScale && parts[1] && parts[1].length > maxScale) {
            // Truncate the scale/fraction part if it exceeds maxScale
            value = `${parts[0]}${decimalSeparator}${parts[1].slice(0, maxScale)}`;
        }

        return value;

        // We only need one unchanging instance of this function
        //eslint-disable-next-line
    }, []);

    return { applyPrecisionAndScale };

};

export { usePrecisionAndScale };
